// Wording for every blocked reason that can reach a coach.
//
// Retired reasons stay here on purpose. Rows written by earlier versions of the
// backend still carry them long after the code that emitted them is gone, so
// deleting an entry here strands those rows with a generic message. This is a
// display contract only; the backend never consults it to decide whether
// generation may proceed.
const BLOCKED_REASON_MESSAGES: Record<string, string> = {
  actor_roles_missing:
    'This generation job has no roles to run under, so artifact generation is unavailable.',
  ambiguous_owner:
    'This session needs one resolved organizer coach before artifact generation is available.',
  ambiguous_subject:
    'This session needs one resolved coachee before artifact generation is available.',
  autosummary_session_date_missing:
    'This session has no date, so automatic summary generation could not schedule it.',
  autosummary_session_too_old:
    'This session is outside the automatic summary window. You can still generate it here.',
  autosummary_source_coach_missing:
    'An earlier attempt could not identify this session’s coach. That has since been fixed, so generating again should work.',
  cancelled_session: 'Artifact generation is unavailable for cancelled sessions.',
  chem_call_reflection_disabled: 'This member is not enabled for chemistry call reflections yet.',
  generation_not_retryable:
    'A previous generation for this session did not finish and will not retry on its own. Contact support if generating again does not help.',
  historical_backfill_not_requested:
    'An earlier pass skipped this session because reflections were not enabled for this member yet.',
  legacy_provenance_ambiguous:
    'This session has ambiguous transcript data, so artifact generation is unavailable.',
  legacy_provenance_missing:
    'This session is missing the transcript data needed to generate an artifact.',
  member_access_required:
    'An earlier pass skipped this session because the member had not granted access yet.',
  member_reflection_members_only_disabled: 'This member is not enabled for reflections yet.',
  member_reflection_session_date_missing:
    'This session has no date, so automatic reflection generation could not schedule it.',
  member_reflection_session_too_old:
    'This session is outside the automatic reflection window. You can still generate it here.',
  member_reflection_session_routed_to_other_profile:
    'Another member reflection profile handles this session type.',
  member_reflection_session_type_unmapped: 'No member reflection is defined for this session type.',
  missing_owner:
    'This session needs a resolved organizer coach before artifact generation is available.',
  missing_planned_end_at:
    'This session is missing its scheduled end time, so artifact generation is unavailable.',
  missing_planned_start_at:
    'This session is missing its scheduled start time, so artifact generation is unavailable.',
  missing_subject: 'This session needs a resolved coachee before artifact generation is available.',
  not_authorized:
    'Only the session organizer or an additional coach can generate artifacts for this session.',
  not_ready: 'This session is not ready for artifact generation yet.',
  profile_not_enabled_for_owner: 'Artifact generation is not enabled for this session.',
  ready_transition_payload_missing:
    'This session is missing the transcript data needed to generate an artifact.',
  session_transcript_source_unavailable:
    'This session is missing the transcript data needed to generate an artifact.',
  stale_readiness:
    'This session’s transcripts changed after this generation was queued, so it was stopped. Generating again uses the current transcripts.',
  terminal_generation_error:
    'A previous generation for this session failed in a way that cannot be retried. Contact support.',
  unsupported_profile: 'Artifact generation is unavailable for this session.',
};

const READINESS_REASON_MESSAGES: Record<string, string> = {
  ambiguous_event: 'This session has ambiguous calendar event data.',
  ambiguous_participant: 'This session has ambiguous participant data.',
  cancelled_session: 'Artifact generation is unavailable for cancelled sessions.',
  no_conversation:
    'This session had no conversation in its transcript, so no artifacts are created.',
  not_transcribed:
    'No transcript was recorded for this session, only Gemini notes, so no artifacts are created. Turn on transcription in Google Meet to get artifacts for future sessions.',
  ready: 'Not generated yet',
  transcript_content_pending: 'Not generated yet',
  unsupported_session: 'Artifact generation is unavailable for this session.',
  waiting_for_attachment: 'This session needs an accepted transcript before artifact generation.',
  waiting_for_event: 'This session is still being matched to a calendar event.',
  waiting_for_participant_resolution:
    'This session needs resolved participants before artifact generation is available.',
  waiting_for_usable_transcript:
    'This session needs an accepted transcript before artifact generation.',
};

export function getArtifactGenerationBlockedMessage(reason?: string | null): string {
  const normalized = reason?.trim();
  if (!normalized) {
    return 'Artifact generation is unavailable for this session.';
  }
  return (
    BLOCKED_REASON_MESSAGES[normalized] ?? 'Artifact generation is unavailable for this session.'
  );
}

function isArtifactGenerationBlockedReason(reason?: string | null): boolean {
  const normalized = reason?.trim();
  return normalized ? Object.hasOwn(BLOCKED_REASON_MESSAGES, normalized) : false;
}

export function getArtifactGenerationReadinessMessage(reason?: string | null): string {
  const normalized = reason?.trim();
  if (!normalized) {
    return 'This session is not ready for artifact generation yet.';
  }
  return (
    READINESS_REASON_MESSAGES[normalized] ??
    'This session is not ready for artifact generation yet.'
  );
}

export function getArtifactGenerationActionabilityMessage(reason?: string | null): string {
  const normalized = reason?.trim();
  if (!normalized) {
    return getArtifactGenerationBlockedMessage(normalized);
  }
  if (isArtifactGenerationBlockedReason(normalized)) {
    return getArtifactGenerationBlockedMessage(normalized);
  }
  return getArtifactGenerationReadinessMessage(normalized);
}
